/**
 * The Help & Support content.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * EDIT THIS FILE to change what the Help page says. Nothing here is wired to
 * component internals - add, remove or reorder freely, and the page follows.
 *
 *   question : phrase it the way a brewery would ask it out loud
 *   path     : the exact clicks, in order. Rendered as Memberships → ⋯ → Make live
 *   note     : the precondition or gotcha. Usually the genuinely useful half -
 *              most support emails are someone hitting a precondition, not
 *              failing to find a button. Optional.
 *   to       : route for the "Take me there" button. Optional; omit when the
 *              destination depends on which record they want.
 *
 * Sections render in the order written, and so do topics within them.
 * ──────────────────────────────────────────────────────────────────────────
 */

export interface HelpTopic {
  question: string
  path: string[]
  note?: string
  to?: string
}

export interface HelpSection {
  title: string
  topics: HelpTopic[]
}

export const HELP_SECTIONS: HelpSection[] = [
  {
    title: 'Getting set up',
    topics: [
      {
        question: 'How do I connect Stripe?',
        path: ['Settings', 'Integrations', 'Connect Stripe'],
        note: 'Do this first. Until Stripe is connected you cannot make any membership live, because patrons would have nowhere to pay.',
        to: '/settings',
      },
      {
        question: 'How do I enable a membership?',
        path: ['Memberships', 'the ⋯ menu on the row', 'Make live'],
        note: 'If that option reads "Connect Stripe to publish" and is greyed out, connect Stripe first.',
        to: '/memberships',
      },
      {
        question: 'How do I add my logo?',
        path: ['Settings', 'Branding', 'Upload logo'],
        note: 'PNG or JPEG, at least 320px on the longest side, under 2MB. Your logo is also required before you can turn on Apple Wallet cards, because Apple puts it on every pass.',
        to: '/settings/branding',
      },
      {
        question: 'How do I set up Apple Wallet cards?',
        path: ['Memberships', 'open the membership', 'Wallet'],
        note: 'Add your logo under Settings → Branding first, or enabling wallet cards will be refused.',
        to: '/memberships',
      },
      {
        question: 'How do I create a membership of my own?',
        path: ['Memberships', 'New membership'],
        note: 'You start with three draft memberships you can edit instead, which is usually quicker than building one from scratch.',
        to: '/memberships/new',
      },
    ],
  },
  {
    title: 'Reaching your members',
    topics: [
      {
        question: 'How do I send a notification to members?',
        path: ['Notify', 'write your message', 'choose an audience', 'Send'],
        note: 'This lands on the lock screen of members who added their Apple Wallet card. You can preview the recipient count before sending.',
        to: '/notify',
      },
      {
        question: 'How do I set up SMS marketing?',
        path: ['Settings', 'SMS marketing', 'Start SMS setup'],
        note: 'SMS is part of the Pro plan. Registration needs your business details and an EIN, and carrier approval takes several business days - so start it well before you need it.',
        to: '/settings/sms',
      },
      {
        question: 'Why can nobody in my audience be texted?',
        path: ['Notify', 'SMS'],
        note: 'Members opt in to marketing texts when they join, so the number grows as people sign up. Existing members who joined before you enabled SMS have not opted in and cannot be texted until they do.',
        to: '/notify',
      },
      {
        question: 'How do I put an announcement in the patron app?',
        path: ['Announcements'],
        note: 'One announcement per brewery, shown at the top of the members’ home screen. Editing it replaces the previous one.',
        to: '/announcements',
      },
    ],
  },
  {
    title: 'Day to day',
    topics: [
      {
        question: 'How do I look up a member at the bar?',
        path: ['Wallet Lookup', 'type their 4-character code'],
        note: 'The code is on their wallet card. You will see their benefits as large buttons you can redeem with one tap.',
        to: '/wallet-lookup',
      },
      {
        question: 'How do I add an employee?',
        path: ['Employees', 'New employee'],
        note: 'Employees can redeem benefits but cannot change settings, pricing or memberships.',
        to: '/employees',
      },
      {
        question: 'Why can patrons not sign up for my membership?',
        path: ['Memberships', 'check the status column'],
        note: 'A membership only accepts signups when it is live. Draft memberships are invisible to patrons. Making one live also requires Stripe to be connected.',
        to: '/memberships',
      },
      {
        question: 'How do I update the card Stitch bills?',
        path: ['Settings', 'Billing', 'Update card'],
        note: 'This is the card for your own Stitch subscription, not the one patrons pay with. To change your plan, email us.',
        to: '/settings/billing',
      },
    ],
  },
]

export const SUPPORT_EMAIL = 'help@stitch.vip'
